/**
 * Custom error types for Kitium Auth
 * All errors inherit from AuthError for consistent error handling
 */

/**
 * Base error class for all Kitium Auth errors
 */
export class AuthError extends Error {
  public readonly code: string
  public readonly statusCode: number
  public readonly details?: Record<string, any>
  public readonly isOperational: boolean
  public cause?: Error

  constructor(
    message: string,
    code: string,
    statusCode: number,
    details?: Record<string, any>,
    isOperational: boolean = true
  ) {
    super(message)
    Object.setPrototypeOf(this, AuthError.prototype)

    this.code = code
    this.statusCode = statusCode
    this.details = details
    this.isOperational = isOperational

    Error.captureStackTrace(this, this.constructor)
  }

  toJSON() {
    return {
      error: this.code,
      message: this.message,
      statusCode: this.statusCode,
      details: this.details
    }
  }
}

/**
 * Validation errors - 400 Bad Request
 * Thrown when input validation fails
 */
export class ValidationError extends AuthError {
  constructor(message: string, details?: Record<string, any>) {
    super(message, 'VALIDATION_ERROR', 400, details)
    Object.setPrototypeOf(this, ValidationError.prototype)
  }
}

/**
 * Authentication errors - 401 Unauthorized
 * Thrown when authentication fails (invalid credentials, missing auth, etc)
 */
export class AuthenticationError extends AuthError {
  constructor(message: string, details?: Record<string, any>) {
    super(message, 'AUTHENTICATION_ERROR', 401, details)
    Object.setPrototypeOf(this, AuthenticationError.prototype)
  }
}

/**
 * Authorization errors - 403 Forbidden
 * Thrown when user lacks required permissions/entitlements
 */
export class AuthorizationError extends AuthError {
  constructor(message: string, details?: Record<string, any>) {
    super(message, 'AUTHORIZATION_ERROR', 403, details)
    Object.setPrototypeOf(this, AuthorizationError.prototype)
  }
}

/**
 * Not found errors - 404 Not Found
 * Thrown when resource is not found
 */
export class NotFoundError extends AuthError {
  constructor(resourceType: string, resourceId?: string) {
    const message = resourceId
      ? `${resourceType} with id '${resourceId}' not found`
      : `${resourceType} not found`

    super(message, 'NOT_FOUND', 404, { resourceType, resourceId })
    Object.setPrototypeOf(this, NotFoundError.prototype)
  }
}

/**
 * Conflict errors - 409 Conflict
 * Thrown when operation conflicts with existing data (e.g., duplicate email)
 */
export class ConflictError extends AuthError {
  constructor(message: string, details?: Record<string, any>) {
    super(message, 'CONFLICT', 409, details)
    Object.setPrototypeOf(this, ConflictError.prototype)
  }
}

/**
 * Rate limit errors - 429 Too Many Requests
 * Thrown when rate limit is exceeded
 */
export class RateLimitError extends AuthError {
  public readonly resetAt: Date
  public readonly retryAfter: number

  constructor(message: string, resetAt: Date) {
    const retryAfter = Math.ceil((resetAt.getTime() - Date.now()) / 1000)
    super(message, 'RATE_LIMIT_EXCEEDED', 429, { resetAt, retryAfter })
    Object.setPrototypeOf(this, RateLimitError.prototype)

    this.resetAt = resetAt
    this.retryAfter = Math.max(1, retryAfter)
  }
}

/**
 * OAuth errors - 401 Unauthorized
 * Thrown when OAuth flow fails
 */
export class OAuthError extends AuthError {
  constructor(message: string, provider: string, details?: Record<string, any>) {
    super(message, 'OAUTH_ERROR', 401, { provider, ...details })
    Object.setPrototypeOf(this, OAuthError.prototype)
  }
}

/**
 * Provider not found errors - 404 Not Found
 * Thrown when OAuth provider is not configured
 */
export class ProviderNotFoundError extends AuthError {
  constructor(providerId: string) {
    super(
      `OAuth provider '${providerId}' not found or not configured`,
      'PROVIDER_NOT_FOUND',
      404,
      { providerId }
    )
    Object.setPrototypeOf(this, ProviderNotFoundError.prototype)
  }
}

/**
 * Token errors - 401 Unauthorized
 * Thrown when token validation fails
 */
export class TokenError extends AuthError {
  constructor(message: string, reason?: string) {
    super(message, 'TOKEN_ERROR', 401, { reason })
    Object.setPrototypeOf(this, TokenError.prototype)
  }
}

/**
 * Session errors - 401 Unauthorized
 * Thrown when session is invalid or expired
 */
export class SessionError extends AuthError {
  constructor(message: string, details?: Record<string, any>) {
    super(message, 'SESSION_ERROR', 401, details)
    Object.setPrototypeOf(this, SessionError.prototype)
  }
}

/**
 * API Key errors - 401 Unauthorized
 * Thrown when API key validation fails
 */
export class ApiKeyError extends AuthError {
  constructor(message: string, details?: Record<string, any>) {
    super(message, 'API_KEY_ERROR', 401, details)
    Object.setPrototypeOf(this, ApiKeyError.prototype)
  }
}

/**
 * Database errors - 500 Internal Server Error
 * Thrown when database operations fail
 */
export class DatabaseError extends AuthError {
  constructor(message: string, originalError?: Error, details?: Record<string, any>) {
    const isOperational = !originalError?.message?.includes('FATAL')
    super(message, 'DATABASE_ERROR', 500, details, isOperational)
    Object.setPrototypeOf(this, DatabaseError.prototype)

    if (originalError) {
      this.cause = originalError
    }
  }
}

/**
 * Configuration errors - 500 Internal Server Error
 * Thrown when configuration is invalid
 */
export class ConfigurationError extends AuthError {
  constructor(message: string, details?: Record<string, any>) {
    super(message, 'CONFIGURATION_ERROR', 500, details, false)
    Object.setPrototypeOf(this, ConfigurationError.prototype)
  }
}

/**
 * Integration errors - 502 Bad Gateway
 * Thrown when external service integration fails (OAuth provider, Stripe, etc)
 */
export class IntegrationError extends AuthError {
  constructor(serviceName: string, message: string, details?: Record<string, any>) {
    super(
      `${serviceName} integration error: ${message}`,
      'INTEGRATION_ERROR',
      502,
      { serviceName, ...details }
    )
    Object.setPrototypeOf(this, IntegrationError.prototype)
  }
}

/**
 * Internal server errors - 500 Internal Server Error
 * Thrown for unexpected errors
 */
export class InternalError extends AuthError {
  constructor(message: string, originalError?: Error) {
    super(message, 'INTERNAL_ERROR', 500, undefined, false)
    Object.setPrototypeOf(this, InternalError.prototype)

    if (originalError) {
      this.cause = originalError
    }
  }
}

/**
 * Utility function to check if error is an AuthError
 */
export function isAuthError(error: any): error is AuthError {
  return error instanceof AuthError
}

/**
 * Utility function to convert any error to AuthError
 */
export function toAuthError(error: unknown): AuthError {
  if (error instanceof AuthError) {
    return error
  }

  if (error instanceof Error) {
    if (error.message.includes('ECONNREFUSED')) {
      return new DatabaseError('Database connection failed', error)
    }

    if (error.message.includes('unique violation')) {
      return new ConflictError('Resource already exists', {
        originalMessage: error.message
      })
    }

    if (error.message.includes('not found')) {
      return new NotFoundError('Resource')
    }

    return new InternalError(error.message, error)
  }

  return new InternalError(String(error))
}

/**
 * Utility function to get HTTP status code from error
 */
export function getStatusCode(error: unknown): number {
  if (error instanceof AuthError) {
    return error.statusCode
  }
  return 500
}

/**
 * Utility function to format error for API response
 */
export function formatErrorResponse(error: unknown) {
  const authError = toAuthError(error)

  return {
    error: authError.code,
    message: authError.message,
    statusCode: authError.statusCode,
    ...(authError.details && { details: authError.details })
  }
}
